import { Howl, Howler } from 'howler';

// Music track imports (M4A/AAC format for better compression)
import welcomeToTheGameUrl from '@/assets/Musique/Welcome to the Game.m4a';
import rollingTheDiceUrl from '@/assets/Musique/Rolling the Dice.m4a';

// SFX imports (M4A/AAC format for better compression)
import dicesSfxUrl from '@/assets/SFX/Dices.m4a';
import gamefindSfxUrl from '@/assets/SFX/Gamefind.m4a';
import goalSfxUrl from '@/assets/SFX/goal.mp3';
import tacleSfxUrl from '@/assets/SFX/Tacle.mp3';
import kickSfxUrl from '@/assets/SFX/kick.mp3';
import jumpSfxUrl from '@/assets/SFX/jump.mp3';
import notifSfxUrl from '@/assets/SFX/notif.mp3';
import poseSfxUrl from '@/assets/SFX/pose.mp3';

/**
 * Music track identifiers
 */
export type MusicTrack = 'welcome' | 'rolling';

/**
 * Sound effect type identifiers
 */
export type SFXType = 'dice' | 'kick' | 'jump' | 'tackle' | 'goal' | 'gamefind' | 'notif' | 'pose';

/**
 * Crossfade duration in milliseconds
 */
const CROSSFADE_DURATION = 500;

/**
 * Total number of audio assets to preload
 */
const TOTAL_ASSETS = 10; // 2 music + 8 SFX

/**
 * AudioService - Singleton class for managing game audio
 *
 * Handles music playback with crossfade transitions and SFX playback.
 * Uses Howler.js for cross-browser audio support including mobile.
 * Implements autoplay unlock mechanism for mobile browsers.
 */
class AudioService {
  private static instance: AudioService | null = null;

  private musicTracks: Map<MusicTrack, Howl>;
  private sfxSounds: Map<SFXType, Howl>;
  private currentTrack: MusicTrack | null = null;
  private currentHowl: Howl | null = null;
  private musicVolume = 1.0;
  private sfxVolume = 1.0;
  private isMusicMuted = false;
  private isSfxMuted = false;
  private _isReady = false;
  private loadedCount = 0;
  private onReadyCallbacks: (() => void)[] = [];

  // Mobile autoplay handling
  private _isAudioUnlocked = false;
  private queuedTrack: MusicTrack | null = null;
  private unlockListenerSetup = false;

  private constructor() {
    const onAssetLoaded = () => {
      this.handleAssetLoaded();
    };

    // Initialize music tracks
    this.musicTracks = new Map<MusicTrack, Howl>([
      [
        'welcome',
        new Howl({
          src: [welcomeToTheGameUrl],
          loop: true,
          preload: true,
          onload: onAssetLoaded
        })
      ],
      [
        'rolling',
        new Howl({
          src: [rollingTheDiceUrl],
          loop: true,
          preload: true,
          onload: onAssetLoaded
        })
      ]
    ]);

    // Initialize SFX sounds with pre-set volume
    this.sfxSounds = new Map<SFXType, Howl>([
      [
        'dice',
        new Howl({
          src: [dicesSfxUrl],
          preload: true,
          volume: this.sfxVolume,
          onload: onAssetLoaded
        })
      ],
      [
        'gamefind',
        new Howl({
          src: [gamefindSfxUrl],
          preload: true,
          volume: this.sfxVolume,
          onload: onAssetLoaded
        })
      ],
      [
        'goal',
        new Howl({
          src: [goalSfxUrl],
          preload: true,
          volume: this.sfxVolume,
          onload: onAssetLoaded
        })
      ],
      [
        'tackle',
        new Howl({
          src: [tacleSfxUrl],
          preload: true,
          volume: this.sfxVolume,
          onload: onAssetLoaded
        })
      ],
      [
        'kick',
        new Howl({
          src: [kickSfxUrl],
          preload: true,
          volume: this.sfxVolume,
          onload: onAssetLoaded
        })
      ],
      [
        'jump',
        new Howl({
          src: [jumpSfxUrl],
          preload: true,
          volume: this.sfxVolume,
          onload: onAssetLoaded
        })
      ],
      [
        'notif',
        new Howl({
          src: [notifSfxUrl],
          preload: true,
          volume: this.sfxVolume,
          onload: onAssetLoaded
        })
      ],
      [
        'pose',
        new Howl({
          src: [poseSfxUrl],
          preload: true,
          volume: this.sfxVolume,
          onload: onAssetLoaded
        })
      ]
    ]);

    // Check initial audio context state
    this.checkAudioContextState();
  }

  /**
   * Get the singleton instance of AudioService
   */
  public static getInstance(): AudioService {
    AudioService.instance ??= new AudioService();
    return AudioService.instance;
  }

  /**
   * Handle individual asset load completion (O(1) instead of O(n))
   */
  private handleAssetLoaded(): void {
    this.loadedCount++;
    if (this.loadedCount >= TOTAL_ASSETS) {
      this._isReady = true;
      this.onReadyCallbacks.forEach((cb) => {
        cb();
      });
      this.onReadyCallbacks = [];
    }
  }

  /**
   * Register a callback to be called when audio is ready
   */
  public onReady(callback: () => void): void {
    if (this._isReady) {
      callback();
    } else {
      this.onReadyCallbacks.push(callback);
    }
  }

  /**
   * Check the current state of the Web Audio API context
   */
  private checkAudioContextState(): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    this._isAudioUnlocked = Howler.ctx ? Howler.ctx.state === 'running' : false;
  }

  /**
   * Returns whether all audio assets are preloaded and ready
   */
  public get isReady(): boolean {
    return this._isReady;
  }

  /**
   * Returns whether audio is unlocked (context is running)
   */
  public get isAudioUnlocked(): boolean {
    this.checkAudioContextState();
    return this._isAudioUnlocked;
  }

  /**
   * Unlock the audio context for mobile browsers
   * Should be called after user interaction
   */
  public async unlockAudio(): Promise<void> {
    const ctx = Howler.ctx;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!ctx || ctx.state === 'running') {
      this._isAudioUnlocked = true;
      this.playQueuedTrack();
      return;
    }

    try {
      await ctx.resume();
      this._isAudioUnlocked = (ctx.state as string) === 'running';
      this.playQueuedTrack();
    } catch {
      this._isAudioUnlocked = false;
    }
  }

  /**
   * Play the queued track if one exists
   */
  private playQueuedTrack(): void {
    if (this.queuedTrack) {
      const track = this.queuedTrack;
      this.queuedTrack = null;
      this.playMusicInternal(track);
    }
  }

  /**
   * Setup document-level listener to unlock audio on first user interaction
   */
  public setupUnlockListener(): void {
    if (this.unlockListenerSetup) {
      return;
    }

    const unlockHandler = () => {
      void this.unlockAudio();
    };

    document.addEventListener('click', unlockHandler, { once: true });
    document.addEventListener('touchstart', unlockHandler, { once: true });

    this.unlockListenerSetup = true;
  }

  /**
   * Internal method to play music, bypassing queue logic
   */
  private playMusicInternal(track: MusicTrack): void {
    const newHowl = this.musicTracks.get(track);
    if (!newHowl) return;

    if (this.currentTrack === track && this.currentHowl?.playing()) {
      return;
    }

    if (this.currentHowl?.playing()) {
      const oldHowl = this.currentHowl;
      const oldVolume = oldHowl.volume();

      oldHowl.fade(oldVolume, 0, CROSSFADE_DURATION);
      setTimeout(() => {
        oldHowl.stop();
      }, CROSSFADE_DURATION);
    }

    const targetVolume = this.isMusicMuted ? 0 : this.musicVolume;
    newHowl.volume(0);
    newHowl.play();
    newHowl.fade(0, targetVolume, CROSSFADE_DURATION);

    this.currentTrack = track;
    this.currentHowl = newHowl;
  }

  /**
   * Play a music track with optional crossfade from current track
   */
  public playMusic(track: MusicTrack): void {
    if (!this.isAudioUnlocked) {
      this.queuedTrack = track;
      this.currentTrack = track;
      return;
    }

    this.playMusicInternal(track);
  }

  /**
   * Stop the currently playing music
   */
  public stopMusic(): void {
    this.queuedTrack = null;

    if (this.currentHowl) {
      const howl = this.currentHowl;
      const currentVolume = howl.volume();

      howl.fade(currentVolume, 0, CROSSFADE_DURATION);
      setTimeout(() => {
        howl.stop();
      }, CROSSFADE_DURATION);

      this.currentHowl = null;
      this.currentTrack = null;
    }
  }

  /**
   * Set the music volume level
   */
  public setMusicVolume(level: number): void {
    this.musicVolume = Math.max(0, Math.min(1, level));

    if (this.currentHowl && !this.isMusicMuted) {
      this.currentHowl.volume(this.musicVolume);
    }
  }

  /**
   * Mute or unmute music
   */
  public muteMusic(muted: boolean): void {
    this.isMusicMuted = muted;

    if (this.currentHowl) {
      this.currentHowl.volume(muted ? 0 : this.musicVolume);
    }
  }

  /**
   * Play a one-shot sound effect
   */
  public playSFX(effect: SFXType): void {
    if (this.isSfxMuted) return;

    const sfx = this.sfxSounds.get(effect);
    if (sfx) {
      sfx.play();
    }
  }

  /**
   * Set the SFX volume level
   */
  public setSFXVolume(level: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, level));

    // Update volume on all SFX sounds
    this.sfxSounds.forEach((sfx) => {
      sfx.volume(this.sfxVolume);
    });
  }

  /**
   * Mute or unmute sound effects
   */
  public muteSFX(muted: boolean): void {
    this.isSfxMuted = muted;
  }

  /**
   * Get whether music is currently muted
   */
  public get musicMuted(): boolean {
    return this.isMusicMuted;
  }

  /**
   * Get whether SFX is currently muted
   */
  public get sfxMuted(): boolean {
    return this.isSfxMuted;
  }

  /**
   * Reset the singleton instance (useful for testing)
   */
  public static resetInstance(): void {
    if (AudioService.instance) {
      AudioService.instance.stopMusic();
      AudioService.instance.queuedTrack = null;
      AudioService.instance.unlockListenerSetup = false;
      AudioService.instance.loadedCount = 0;
      AudioService.instance._isReady = false;
      AudioService.instance.onReadyCallbacks = [];
      Howler.unload();
      AudioService.instance = null;
    }
  }
}

// Export singleton getter
export const getAudioService = AudioService.getInstance.bind(AudioService);

// Export class for testing purposes
export { AudioService };
